import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, animate, motion, useMotionValue } from "framer-motion";
import {
  Home,
  Lightbulb,
  GitBranch,
  LayoutGrid,
  Gamepad2,
  Globe,
  Volume2,
  VolumeX,
  Music,
  Vault,
  UserRound,
} from "lucide-react";
import { IDEVRA, CATEGORIES } from "@/data/idevra";
import { sfx } from "@/lib/sound";
import { buzz } from "@/lib/haptics";
import { useCoarsePointer } from "@/lib/useCoarsePointer";
import { trophies, trackProgress, bumpKeys, useTrophyCount } from "@/lib/trophies";
import TrophyToasts from "@/pages/idevra/TrophyToasts";
import TrophyChip from "@/pages/idevra/TrophyChip";
import LiveMark from "@/pages/idevra/LiveMark";
import AttractMode from "@/pages/idevra/AttractMode";
import GlobalTrophyTicker from "@/pages/idevra/GlobalTrophyTicker";
import AretaWidget from "@/pages/idevra/AretaWidget";
import { HomePanel, PhilosophyPanel, ModelPanel, CoreAreasPanel, VisionPanel } from "@/pages/idevra/Panels";
import VenturesPanel from "@/pages/idevra/VenturesPanel";
import VaultPanel from "@/pages/idevra/VaultPanel";
import FounderPanel from "@/pages/idevra/FounderPanel";
import VentureOverlay from "@/pages/idevra/VentureOverlay";
import CommsPanel from "@/pages/idevra/CommsPanel";
import AudioMenu from "@/pages/idevra/AudioMenu";
import SoundTestRoom from "@/pages/idevra/SoundTestRoom";
import KonamiFX from "@/pages/idevra/KonamiFX";
import RetroSkin from "@/pages/idevra/RetroSkin";

const KONAMI = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];

const ICONS = { home: Home, founder: UserRound, philosophy: Lightbulb, model: GitBranch, core: LayoutGrid, ventures: Gamepad2, vault: Vault, vision: Globe };
const SPRING = { type: "spring", stiffness: 300, damping: 30 };

const listLength = (id) =>
  id === "ventures"
    ? IDEVRA.ventures.length
    : id === "core"
    ? IDEVRA.coreAreas.length
    : id === "vault"
    ? IDEVRA.patents.items.length + IDEVRA.trademarks.items.length
    : id === "founder"
    ? IDEVRA.founder.quests.length
    : 0;

function useItemWidth() {
  const [w, setW] = useState(() => (window.innerWidth < 640 ? 112 : 172));
  useEffect(() => {
    const on = () => setW(window.innerWidth < 640 ? 112 : 172);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);
  return now;
}

export default function Console({ initialVentureSlug, initialCategoryId }) {
  const [cat, setCat] = useState(0);
  const [focusIdx, setFocusIdx] = useState(0);
  const [overlay, setOverlay] = useState(null);
  const [attract, setAttract] = useState(false);
  const [comms, setComms] = useState(false);
  const [soundTest, setSoundTest] = useState(false);
  const [konamiFx, setKonamiFx] = useState(false);
  const [retro, setRetro] = useState(false);
  const prevThemeRef = useRef(null);
  const [pad, setPad] = useState(false);
  const [muted, setMuted] = useState(sfx.muted);
  const [music, setMusic] = useState(() => localStorage.getItem("idevra_music") !== "0");
  const [launchFx, setLaunchFx] = useState(0);
  const launchTimer = useRef(null);
  const itemW = useItemWidth();
  const now = useClock();
  const stateRef = useRef({});
  const konamiRef = useRef([]);
  stateRef.current = { cat, focusIdx, overlay, attract, comms, soundTest };

  useEffect(() => {
    trophies.unlock("system-online");
    trackProgress("idevra_cats", "home", CATEGORIES.length, "explorer");
    if (localStorage.getItem("idevra_music") !== "0") sfx.startAmbient();
    if (initialVentureSlug) {
      const idx = IDEVRA.ventures.findIndex((v) => v.slug === initialVentureSlug);
      if (idx >= 0) {
        setCat(CATEGORIES.findIndex((c) => c.id === "ventures"));
        setFocusIdx(idx);
        setOverlay(IDEVRA.ventures[idx]);
        trackProgress("idevra_cats", "ventures", CATEGORIES.length, "explorer");
        trackProgress("idevra_seen_ventures", initialVentureSlug, IDEVRA.ventures.length, "collector");
      }
    } else if (initialCategoryId) {
      const cIdx = CATEGORIES.findIndex((c) => c.id === initialCategoryId);
      if (cIdx >= 0) {
        setCat(cIdx);
        trackProgress("idevra_cats", initialCategoryId, CATEGORIES.length, "explorer");
      }
    }
    return () => sfx.stopAmbient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("attract");
    const idleMs = (q ? Number(q) : 45) * 1000;
    let t;
    const arm = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        if (!stateRef.current.overlay && !stateRef.current.attract) setAttract(true);
      }, idleMs);
    };
    const evs = ["mousemove", "keydown", "pointerdown", "wheel", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    arm();
    return () => {
      clearTimeout(t);
      evs.forEach((e) => window.removeEventListener(e, arm));
    };
  }, []);

  const goCat = (next, silent = false) => {
    setCat(next);
    setFocusIdx(0);
    if (!silent) sfx.tick();
    trackProgress("idevra_cats", CATEGORIES[next].id, CATEGORIES.length, "explorer");
  };

  const isTouch = useCoarsePointer();
  const ribbonX = useMotionValue(-cat * itemW);
  const dialRef = useRef({ active: false, idx: 0, timer: null });

  useEffect(() => {
    const ctrl = animate(ribbonX, -cat * itemW, SPRING);
    return () => ctrl.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, itemW]);

  useEffect(() => {
    const unsub = ribbonX.on("change", (v) => {
      const d = dialRef.current;
      if (!d.active) return;
      const idx = Math.min(CATEGORIES.length - 1, Math.max(0, Math.round(-v / itemW)));
      if (idx !== d.idx) {
        d.idx = idx;
        sfx.tick();
        buzz();
      }
    });
    return unsub;
  }, [ribbonX, itemW]);

  const onDialStart = () => {
    const d = dialRef.current;
    clearTimeout(d.timer);
    d.active = true;
    d.idx = stateRef.current.cat;
  };

  const onDialEnd = (_e, info) => {
    const projected = ribbonX.get() + info.velocity.x * 0.18;
    const next = Math.min(CATEGORIES.length - 1, Math.max(0, Math.round(-projected / itemW)));
    const d = dialRef.current;
    d.timer = setTimeout(() => {
      d.active = false;
    }, 700);
    if (next !== stateRef.current.cat) goCat(next, true);
    else animate(ribbonX, -next * itemW, SPRING);
  };

  const [padToast, setPadToast] = useState(null);
  const padToastTimer = useRef(null);

  useEffect(() => {
    const update = () =>
      setPad(navigator.getGamepads && [...navigator.getGamepads()].some(Boolean));
    const onConnect = (e) => {
      update();
      const name = (e.gamepad?.id || "GAMEPAD")
        .replace(/\s*\(.*\)\s*$/, "")
        .trim()
        .toUpperCase();
      setPadToast(name);
      sfx.clank();
      clearTimeout(padToastTimer.current);
      padToastTimer.current = setTimeout(() => setPadToast(null), 4000);
    };
    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", update);
    update();
    return () => {
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", update);
      clearTimeout(padToastTimer.current);
    };
  }, []);

  useEffect(() => {
    const resume = () => sfx.unlock();
    window.addEventListener("pointerdown", resume, { once: true });
    window.addEventListener("keydown", resume, { once: true });
    return () => {
      window.removeEventListener("pointerdown", resume);
      window.removeEventListener("keydown", resume);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (stateRef.current.attract) return;
      const kb = konamiRef.current;
      kb.push(e.key.length === 1 ? e.key.toLowerCase() : e.key);
      if (kb.length > KONAMI.length) kb.shift();
      if (kb.length === KONAMI.length && KONAMI.every((k, i) => kb[i] === k)) {
        konamiRef.current = [];
        sfx.konamiCinematic();
        trophies.unlock("konami");
        setKonamiFx(true);
        return;
      }
      if (stateRef.current.soundTest) {
        if (e.key === "Escape") {
          sfx.back();
          setSoundTest(false);
        }
        return;
      }
      if (stateRef.current.comms) {
        if (e.key === "Escape") {
          sfx.back();
          setComms(false);
        }
        return;
      }
      const { cat: c, focusIdx: f, overlay: o } = stateRef.current;
      if (o) {
        if (e.key === "Escape" || e.key === "Backspace") {
          sfx.back();
          setOverlay(null);
          bumpKeys();
        }
        return;
      }
      const id = CATEGORIES[c].id;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const len = listLength(id);
      const handled = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Enter"].includes(e.key);
      if (e.key === "ArrowRight" && c < CATEGORIES.length - 1) goCat(c + 1);
      else if (e.key === "ArrowLeft" && c > 0) goCat(c - 1);
      else if (e.key === "ArrowDown" && len) {
        setFocusIdx((f + 1) % len);
        sfx.tick();
      } else if (e.key === "ArrowUp" && len) {
        setFocusIdx((f - 1 + len) % len);
        sfx.tick();
      } else if (e.key === "Enter" && id === "ventures") {
        sfx.jingle(IDEVRA.ventures[f].slug);
        setOverlay(IDEVRA.ventures[f]);
        trackProgress("idevra_seen_ventures", IDEVRA.ventures[f].slug, IDEVRA.ventures.length, "collector");
      } else if (e.key.toLowerCase() === "c") {
        sfx.clank();
        setComms(true);
      }
      if (handled) bumpKeys();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openVenture = (v) => {
    sfx.jingle(v.slug);
    setOverlay(v);
    trackProgress("idevra_seen_ventures", v.slug, IDEVRA.ventures.length, "collector");
  };

  const fireLaunch = () => {
    sfx.psLaunch();
    setLaunchFx((n) => n + 1);
    clearTimeout(launchTimer.current);
    launchTimer.current = setTimeout(() => setLaunchFx(0), 2600);
    goCat(0, true);
  };

  const toggleSound = () => {
    const m = sfx.toggleMute();
    setMuted(m);
    if (!m) sfx.clank();
  };

  const toggleMusic = () => {
    setMusic((on) => {
      const next = !on;
      localStorage.setItem("idevra_music", next ? "1" : "0");
      if (next) sfx.startAmbient();
      else sfx.stopAmbient();
      return next;
    });
    sfx.clank();
  };

  const openComms = () => {
    sfx.clank();
    setComms(true);
  };

  useTrophyCount();
  const hasKonami = trophies.has("konami");

  const startRetro = () => {
    if (!retro) prevThemeRef.current = sfx.theme;
    sfx.theme = "retro";
    setRetro(true);
  };

  const endRetro = () => {
    sfx.theme = prevThemeRef.current || "classic";
    setRetro(false);
    sfx.back();
  };

  const activeId = CATEGORIES[cat].id;

  return (
    <motion.div
      className={`absolute inset-0 flex flex-col ${retro ? "iv-retro86" : ""}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
      data-testid="idevra-console"
    >
      <div className="iv-sheen" />
      <div className="iv-grid" />
      <div className="iv-scan" />
      <div className="iv-grain" />

      {/* Status bar */}
      <header
        className="relative z-30 flex items-center justify-between px-5 sm:px-10 pt-5"
        data-testid="status-bar"
      >
        <button
          type="button"
          data-testid="logo-home-btn"
          onClick={fireLaunch}
          className="flex items-center gap-3 text-left"
          aria-label="Go to home"
        >
          <span className={`relative inline-block ${launchFx ? "iv-launch-active" : ""}`}>
            <LiveMark className="w-7" />
            {launchFx > 0 && (
              <span key={launchFx} className="iv-launch-burst" aria-hidden="true">
                <span className="iv-burst-core" />
                <span className="iv-burst-ring r1" />
                <span className="iv-burst-ring r2" />
                <span className="iv-burst-ring r3" />
              </span>
            )}
          </span>
          <div className="hidden sm:block">
            <div className="f-syncopate text-[10px] tracking-[0.4em] text-[#0F172A]">IDEVRA</div>
            <div className="f-mono text-[8px] tracking-[0.3em] text-[#94A3B8]">GLOBAL HOLDINGS · OS v1.07</div>
          </div>
        </button>
        <div className="flex items-center gap-3 sm:gap-4">
          {pad && (
            <span
              data-testid="gamepad-indicator"
              className="iv-glass flex h-10 items-center gap-2 rounded-full px-3.5 text-[#0284C7]"
            >
              <Gamepad2 size={15} />
              <span className="f-mono hidden text-[8px] tracking-[0.2em] sm:inline">CONNECTED</span>
            </span>
          )}
          <TrophyChip />
          {hasKonami && (
            <button
              type="button"
              data-testid="retro86-toggle-btn"
              onClick={() => {
                sfx.clank();
                if (retro) endRetro();
                else startRetro();
              }}
              aria-label={retro ? "Exit 1986 retro mode" : "Enter 1986 retro mode"}
              title="RETRO-86 MODE — your Konami reward"
              className={`iv-glass flex h-10 w-10 items-center justify-center rounded-full f-orbitron text-[11px] font-black transition-colors ${
                retro ? "text-[#D97706]" : "text-[#475569] hover:text-[#0284C7]"
              }`}
            >
              86
            </button>
          )}
          <div className="hidden sm:block text-right">
            <div className="f-orbitron text-sm font-semibold tabular-nums text-[#0F172A]">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="f-mono text-[8px] tracking-[0.25em] text-[#94A3B8] uppercase">
              {now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}
            </div>
          </div>
          <button
            type="button"
            data-testid="sound-toggle-btn"
            onClick={toggleSound}
            aria-label={muted ? "Unmute" : "Mute"}
            className="iv-glass flex h-10 w-10 items-center justify-center rounded-full text-[#475569] hover:text-[#0284C7] transition-colors"
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button
            type="button"
            data-testid="music-toggle-btn"
            onClick={toggleMusic}
            aria-label={music ? "Music off" : "Music on"}
            className={`iv-glass relative flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              music ? "text-[#0284C7]" : "text-[#94A3B8]"
            }`}
          >
            <Music size={16} />
            {!music && <span className="iv-music-slash" />}
          </button>
          <AudioMenu
            onOpenSoundTest={() => {
              sfx.clank();
              setSoundTest(true);
            }}
          />
        </div>
      </header>

      {/* XMB category ribbon */}
      <nav className="relative z-20 mt-6 sm:mt-8" data-testid="xmb-bar">
        <motion.div
          className="flex"
          data-testid="xmb-dial"
          style={{ paddingLeft: "max(24px, 11vw)", "--xmb-w": `${itemW}px`, x: ribbonX }}
          drag={isTouch ? "x" : false}
          dragConstraints={{ left: -(CATEGORIES.length - 1) * itemW, right: 0 }}
          dragElastic={0.12}
          dragMomentum={false}
          onDragStart={onDialStart}
          onDragEnd={onDialEnd}
        >
          {CATEGORIES.map((c, i) => {
            const Icon = ICONS[c.id];
            const active = i === cat;
            return (
              <motion.button
                key={c.id}
                type="button"
                data-testid={`xmb-category-${c.id}`}
                onClick={() => goCat(i)}
                onMouseEnter={() => i !== cat && sfx.tick()}
                className={`iv-xmb-item ${active ? "active" : ""}`}
                animate={{ opacity: active ? 1 : 0.38, scale: active ? 1.12 : 0.88 }}
                transition={SPRING}
              >
                <span className="iv-xmb-icon">
                  <Icon size={22} strokeWidth={1.6} color={active ? "#0284C7" : "#64748B"} />
                </span>
                <span className="iv-xmb-label">{c.label}</span>
              </motion.button>
            );
          })}
        </motion.div>
      </nav>

      {/* Content */}
      <main className="iv-panel-scroll relative z-10 flex-1 pt-8 sm:pt-10 pb-24">
        <AnimatePresence mode="wait">
          <motion.section
            key={activeId}
            data-testid={`panel-${activeId}`}
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-full max-w-[1180px] px-5 sm:px-12"
          >
            {activeId === "home" && <HomePanel />}
            {activeId === "founder" && <FounderPanel focusIdx={focusIdx} onOpenComms={openComms} />}
            {activeId === "philosophy" && <PhilosophyPanel />}
            {activeId === "model" && <ModelPanel />}
            {activeId === "core" && <CoreAreasPanel focusIdx={focusIdx} />}
            {activeId === "ventures" && (
              <VenturesPanel focusIdx={focusIdx} setFocusIdx={setFocusIdx} onOpen={openVenture} />
            )}
            {activeId === "vault" && <VaultPanel focusIdx={focusIdx} />}
            {activeId === "vision" && <VisionPanel />}
          </motion.section>
        </AnimatePresence>
      </main>

      {/* Hints bar */}
      <footer
        className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-5 sm:px-10 py-4"
        data-testid="hints-bar"
      >
        <div className="iv-glass flex items-center gap-5 rounded-full px-5 py-2.5">
          <span className="flex items-center gap-2 f-mono text-[9px] tracking-[0.2em] text-[#475569]">
            <span className="iv-hint-glyph blue">✕</span> SELECT
          </span>
          <span className="hidden sm:flex items-center gap-2 f-mono text-[9px] tracking-[0.2em] text-[#475569]">
            <span className="iv-hint-glyph">○</span> BACK
          </span>
          <span className="flex items-center gap-2 f-mono text-[9px] tracking-[0.2em] text-[#475569]">
            <span className="f-mono text-[10px] text-[#94A3B8]">◄ ►</span> NAVIGATE
          </span>
          <button
            type="button"
            data-testid="comms-open-btn"
            onClick={openComms}
            className="flex items-center gap-2 f-mono text-[9px] tracking-[0.2em] text-[#475569] transition-colors hover:text-[#0284C7]"
          >
            <span className="iv-hint-glyph">△</span> COMMS
          </button>
        </div>
        <div className="hidden md:flex items-center gap-5">
          <GlobalTrophyTicker />
          <div className="f-mono text-[9px] tracking-[0.3em] text-[#94A3B8]">
            © {new Date().getFullYear()} IDEVRA GLOBAL HOLDINGS
          </div>
        </div>
      </footer>

      <TrophyToasts />

      <AretaWidget />

      <AnimatePresence>
        {konamiFx && (
          <KonamiFX
            onDone={() => {
              setKonamiFx(false);
              startRetro();
            }}
          />
        )}
      </AnimatePresence>

      {retro && <RetroSkin onExit={endRetro} />}

      <AnimatePresence>
        {attract && <AttractMode onExit={() => setAttract(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {padToast && (
          <motion.div
            data-testid="controller-toast"
            className="fixed left-1/2 top-16 z-50"
            initial={{ opacity: 0, y: -24, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -16, x: "-50%" }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
          >
            <div className="iv-glass flex items-center gap-3 rounded-full px-5 py-3 shadow-xl">
              <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-[#0284C7]/10">
                <Gamepad2 size={15} color="#0284C7" />
                <span className="absolute inset-0 animate-ping rounded-full bg-[#38BDF8]/30" />
              </span>
              <span>
                <span className="block f-mono text-[8px] tracking-[0.35em] text-[#0284C7]">
                  CONTROLLER LINKED
                </span>
                <span className="block f-orbitron text-[10px] font-bold text-[#0F172A]">{padToast}</span>
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {soundTest && (
          <SoundTestRoom
            onClose={() => {
              sfx.back();
              setSoundTest(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {comms && (
          <CommsPanel
            onClose={() => {
              sfx.back();
              setComms(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {overlay && (
          <VentureOverlay
            venture={overlay}
            onClose={() => {
              sfx.back();
              setOverlay(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
